<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="プロセス追加" v-if="isEditable">
        <v-btn icon @click="openCreateDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>
      <v-spacer/>
      <toolbar-inline>
        <v-text-field
            v-model="searchText"
            clearable
            placeholder="検索"/>
      </toolbar-inline>
    </template>

    <draggable-el-table
        :value="processComponentModelsViewFiltered"
        @end="handleDragEnd">
      <el-table
          class="no-padding"
          row-key="id"
          :data="processComponentModelsViewFiltered"
          :height="tabItemBounds.height"
          border>
        <el-table-column
            align="center"
            width="25"
            v-if="isEditable"
            key="handle">
          <v-icon class="handle">mdi-menu</v-icon>
        </el-table-column>
        <el-table-column
            width="50"
            align="center"
            prop="index"
            label="追番"
            key="index">
        </el-table-column>
        <el-table-column
            min-width="25"
            label="プロセス構成モデル名"
            key="name">
          <template #default="{row}">
            <table-inline>
              <v-text-field
                  v-model="row.name"
                  hide-details
                  dense
                  :readonly="!isEditable"
                  @change="handleProcessComponentChange(row)">
                <template #append-outer>
                  <error-popover :errors="row.errors" class="mr-2"></error-popover>
                </template>
              </v-text-field>
            </table-inline>
          </template>
        </el-table-column>
        <el-table-column
            class-name="description"
            label="説明"
            key="description">
          <template #default="{row}">
            <table-inline>
              <description-input
                  v-model="row.description"
                  :readonly="!isEditable"
                  @change="handleProcessComponentChange(row)">
              </description-input>
            </table-inline>
          </template>
        </el-table-column>
        <el-table-column
            width="40"
            align="center"
            fixed="right">
          <template #default="{row}">
            <icon-btns>
              <icon-btn
                  v-if="isEditable"
                  type="delete"
                  @click="openDeleteDialog(row)">
              </icon-btn>
            </icon-btns>
          </template>
        </el-table-column>
      </el-table>
    </draggable-el-table>

    <!-- プロセス作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate"
        title-text="プロセス作成"
        @close="closeCreateDialog">
      <v-row>
        <v-col cols="12">
          <v-text-field
              autofocus
              v-model="dialogCreate.name"
              label="プロセス構成モデル名"
              hint="後から変更が可能です"
              :rules="[v => !!v || 'プロセス構成モデル名が必要です']"/>
        </v-col>
        <v-col cols="12">
          <v-textarea
              v-model="dialogCreate.description"
              label="説明"
              messages="複数行入力可"
              rows="1"
              auto-grow/>
        </v-col>
      </v-row>
    </simple-dialog>

    <!-- プロセス削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete"
        title-text="プロセス削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のプロセス構成モデルを削除します。本当によろしいですか？"
        @close="closeDeleteDialog">
      <v-row>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.name"
              label="プロセス構成モデル名"/>
        </v-col>
        <v-col cols="12">
          <text-field-like
              v-model="dialogDelete.description"
              label="説明"
              placeholder="未設定"
              multi-line/>
        </v-col>
      </v-row>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import PathInfoMixin from "@/router/PathInfoMixin";
import {mapActions, mapGetters, mapMutations} from "vuex";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import DraggableElTable from "@/components/atoms/DraggableElTable.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import DescriptionInput from "@/components/molecules/DescriptionInput.vue";
import mutationProcessComponentModelAdd from "@/gql/ServiceComponentModel/ProcessComponentModelAdd.gql";
import mutationProcessComponentModelRemove from "@/gql/ServiceComponentModel/ProcessComponentModelRemove.gql";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import ErrorPopover from "@/components/molecules/ErrorPopover.vue";
import {maxErrorLevel} from "@/components/molecules/ErrorMessage";

export default {
  name: 'ProcessComponentModelList',
  components: {
    ErrorPopover,
    ToolbarInline,
    TextFieldLike,
    IconBtn, IconBtns, TabItemTemplate, DescriptionInput, TableInline, SimpleDialog, DraggableElTable},
  mixins: [PathInfoMixin],
  props: {
    dictionary: Object,
    serviceComponentModel: Object,
  },
  inject: [
    "tabItemBounds"
  ],
  data() {
    return {
      searchText: null,
      dialogCreate: false,
      dialogDelete: false,
    };
  },
  computed: {
    ...mapGetters({
      entities: 'hds/entities',
    }),
    processComponentModels() {
      return this.entities.processComponentModel
          .find({serviceComponentModelId: this.serviceComponentModel.id})
          .sort((a, b) => a.index - b.index)
    },
    processComponentModelsView() {
      return this.processComponentModels.map(pcm => ({
        ...pcm,
        errors: this.buildErrors(pcm),
      }));
    },
    processComponentModelsViewFiltered() {
      if (this.searchText) {
        return this.processComponentModelsView.filter(d =>
            d.name.indexOf(this.searchText) !== -1 ||
            d.description.indexOf(this.searchText) !== -1);
      }
      return this.processComponentModelsView;
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
    }),
    ...mapActions({
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    handleProcessComponentChange(property) {
      const _e = this.entities;
      _e.processComponentModel.update(property);
      this.$emit('dirty');
    },
    buildErrors(pcm) {
      const errors = [];
      if(this.entities.processOperation.count({processComponentModelId: pcm.id}) === 0) {
        errors.push({
          level: 'warn',
          message: 'プロセス手順定義との紐付きのないプロセス構成モデルです',
        });
      }
      return errors;
    },
    openCreateDialog() {
      this.dialogCreate = {
        serviceComponentModelId: this.serviceComponentModel.id,
        name: '',
        description: '',
        index: this.processComponentModels
            .map(p => p.index)
            .reduce((acc, current) => Math.max(acc, current), 0) + 1,
        processOperations: []
      };
    },
    closeCreateDialog(apply) {
      if (apply) {
        const newEntity = this.dialogCreate;
        this.showSnackbar({
          message: `プロセス構成モデル「${newEntity.name}」を作成中...`,
          timeout: false,
          loading: true
        });
        this.gqlMutateAndLoad({
              mutation: mutationProcessComponentModelAdd,
              variables: {
                addProcessComponentModel: [newEntity]
              },
              mapping: {
                processComponentModel: 'processComponentModels',
              }
            })
            .then(data => {
              this.showSnackbar({
                message: `プロセス構成モデル「${newEntity.name}」を作成しました`
              });
              this.dialogCreate = false;
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `プロセス構成モデル「${newEntity.name}」の作成に失敗しました`
              });
            });
      } else {
        this.dialogCreate = false;
      }
    },
    openDeleteDialog(row) {
      this.dialogDelete = row;
    },
    closeDeleteDialog(apply) {
      const _e = this.entities;
      if (apply) {
        const removeEntity = this.dialogDelete;
        this.showSnackbar({
          message: `プロセス構成モデル「${removeEntity.name}」削除中...`,
          timeout: false,
          loading: true
        });
        this.gqlMutate({
              mutation: mutationProcessComponentModelRemove,
              variables: {
                removeProcessComponentModelIds: [removeEntity.id]
              }
            })
            .then(data => {
              _e.processComponentModel.remove(removeEntity);

              // index を再採番する(hdsでも同様の処理が行われるので表示上のみ更新する)
              let currentIndex = 1;
              this.processComponentModels.forEach(pcm => {
                if(pcm.id !== removeEntity.id) {
                  pcm.index = currentIndex++;
                }
              });

              this.showSnackbar({
                message: `プロセス構成モデル「${removeEntity.name}」が削除されました`
              });
              this.dialogDelete = false;
            }, () => {
              this.showSnackbar({
                color: 'failed',
                message: `プロセス構成モデル「${removeEntity.name}」の削除に失敗しました`
              });
            });
      } else {
        this.dialogDelete = false;
      }
    },
    handleDragEnd(e) {
      const _e = this.entities;
      const processComponents = this.processComponentModels;
      const oldIdx = e.oldDraggableIndex;
      const newIdx = e.newDraggableIndex;
      if (oldIdx < newIdx) {
        for (let i = oldIdx + 1; i <= newIdx; i++) {
          const processComp = processComponents[i];
          processComp.index--;
          _e.processComponentModel.update(processComp);
        }
        const processComp = processComponents[oldIdx];
        processComp.index = newIdx + 1;
        _e.processComponentModel.update(processComp);
        this.$emit('dirty');
      } else if (newIdx < oldIdx) {
        for (let i = newIdx; i < oldIdx; i++) {
          const processComp = processComponents[i];
          processComp.index++;
          _e.processComponentModel.update(processComp);
        }
        const processComp = processComponents[oldIdx];
        processComp.index = newIdx + 1;
        _e.processComponentModel.update(processComp);
        this.$emit('dirty');
      }
    }
  },
  watch: {
    processComponentModels: {
      immediate: true,
      handler() {
        this.$emit('update-error-level');
      }
    }
  }
}
</script>
